import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate } from 'react-router';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_REWARDS_FIXTURE_LEAGUE_QUALITY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { qualityNames } from '@sorare/core/src/lib/players';
import {
  getTreeSchemaFromSo5Fixture,
  getTreeSelection,
} from '@sorare/core/src/lib/rewards';
import { Tree } from '@sorare/core/src/routing/Tree';

import type {
  RewardsFixtureLayoutQuery,
  RewardsFixtureLayoutQueryVariables,
} from './__generated__/index.graphql';

const qualities = Object.entries(qualityNames).reduce<{
  [key: string]: string;
}>((sum, [slug, name]) => {
  sum[name] = slug;
  return sum;
}, {});

const rarities = Object.entries(scarcityNames).reduce<{
  [key: string]: string;
}>((sum, [slug, name]) => {
  if (name in sum) return sum;
  sum[name] = slug;
  return sum;
}, {});

const REWARDS_FIXTURE_LAYOUT_QUERY = gql`
  query RewardsFixtureLayoutQuery($fixture: String!) {
    so5 {
      so5Fixture(slug: $fixture) {
        slug
        startDate
        endDate
        so5Leagues {
          slug
          displayName
          so5Leaderboards {
            slug
            rewardsConfig {
              ranking {
                id
                cards {
                  quality
                }
              }
              conditional {
                id
                cards {
                  quality
                }
              }
            }
          }
          ...getTreeSelection_so5League
        }
        ...getTreeSchemaFromSo5Fixture_so5Fixture
      }
    }
  }
  ${getTreeSchemaFromSo5Fixture.fragments.so5Fixture}
  ${getTreeSelection.fragments.so5League}
` as TypedDocumentNode<
  RewardsFixtureLayoutQuery,
  RewardsFixtureLayoutQueryVariables
>;

type Props = {
  fixture: string;
  league?: string;
  quality?: string;
  rarity?: string;
};

export const RewardsFixtureLayout = ({
  fixture,
  league,
  quality,
  rarity,
}: Props) => {
  const { data } = useQuery(REWARDS_FIXTURE_LAYOUT_QUERY, {
    variables: {
      fixture,
    },
  });
  const { so5Fixture } = data?.so5 || {};

  const navigate = useNavigate();
  const { formatDate } = useIntl();
  const { generateSportContextPath } = useSportContext();

  if (!so5Fixture) {
    return null;
  }

  const { startDate, endDate, so5Leagues = [] } = so5Fixture;

  const treeSchema = getTreeSchemaFromSo5Fixture(so5Fixture);

  const selected = getTreeSelection({
    schema: treeSchema,
    so5Leagues,
    league,
    quality,
    rarity,
  });

  return (
    <FullWidthContainer>
      <PageHeader
        text={{
          id: 'Rewards.Fixture.Layout.PlayersPool',
          defaultMessage: 'Players pool',
        }}
      />
      <Tree
        title={
          <HeadlineL as="p">
            <FormattedMessage
              {...fantasy.gameWeek}
              values={{
                gameWeek: (
                  <>
                    {formatDate(startDate, { month: 'short' })}
                    &nbsp;
                    {formatDate(startDate, { day: 'numeric' })}-
                    {formatDate(endDate, { day: 'numeric' })}
                  </>
                ),
              }}
            />
          </HeadlineL>
        }
        schema={treeSchema}
        selected={selected}
        onSelect={([selectedLeague, selectedQuality, selectedRarity]) => {
          const leagueSlug = so5Leagues.find(
            l => l.displayName === selectedLeague
          )?.slug;
          const selectedQualityName = selectedQuality
            ? qualities[selectedQuality].toLowerCase()
            : undefined;
          const selectedRarityName = selectedRarity
            ? rarities[selectedRarity].toLowerCase()
            : undefined;
          if (!leagueSlug || !selectedQualityName || !selectedRarityName) {
            return;
          }
          navigate(
            generateSportContextPath(
              ANY_SPORT_REWARDS_FIXTURE_LEAGUE_QUALITY_RARITY,
              {
                params: {
                  fixture: so5Fixture.slug,
                  league: leagueSlug,
                  quality: selectedQualityName,
                  rarity: selectedRarityName,
                },
              }
            ),
            {
              replace: true,
            }
          );
        }}
      >
        <Outlet />
      </Tree>
    </FullWidthContainer>
  );
};
